import { useCallback, useMemo, useState } from "react";
import { QuestionProps } from "./Props";
import { Button, Alert, Loading, Modal, Icon } from "@aidkitorg/component-library";
import Form from "react-bootstrap/Form";
import { usePost } from "../API";
import { safeParse } from "../Util";
import { useLocalizedStrings } from "../Localization";

type CheckZelleAliasResponse = {
  alias?: string;
  firstName?: string;
  lastName?: string;
  financialOrganizationName?: string;
  organizationType?: string;
  aliasEnrollmentStatus?: string;
  error?: string;
};

export function ZelleIntake(props: QuestionProps) {
  const targetField = props["Target Field"] ?? "";

  // Options have the field name as `value` and the actual phone/email as `label`
  const contactOptions = useMemo(() => {
    const fields: string[] = safeParse(props.Metadata || '{}').contact_options ?? [];
    return fields
      .map((field) => {
        const value = props.info?.[field];
        return value ? { label: value, value: field } : null;
      })
      .filter((opt): opt is { label: string; value: string } => opt !== null);
  }, [props.Metadata, props.info]);

  const savedAliasValue = props.info?.[targetField] ?? "";
  // On mount, find which field name corresponds to the saved alias value
  const initialSelectedField = useMemo(() => {
    if (!savedAliasValue) return "";
    const fields: string[] = safeParse(props.Metadata || '{}').contact_options ?? [];
    return fields.find((f) => props.info?.[f] === savedAliasValue) ?? "";
  }, []);

  const [alias, setAlias] = useState<string>(savedAliasValue);
  const [selectedField, setSelectedField] = useState<string>(initialSelectedField);
  const [isChecking, setIsChecking] = useState(false);
  const [result, setResult] = useState<CheckZelleAliasResponse | null>(
    props.info?.[targetField] ? { aliasEnrollmentStatus: "TOKEN_ENROLLED" } : null
  );
  const [checkError, setCheckError] = useState<string | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const radioGroupName = `zelle-intake-${targetField || "contact-options"}`;

  const L = useLocalizedStrings();
  const checkZelleAlias = usePost("/payments/check_zelle_alias");

  const isAliasValid = selectedField !== "";
  const enrollmentStatus = result?.aliasEnrollmentStatus;
  const isEnrolled = enrollmentStatus === "TOKEN_ENROLLED";
  const isAlreadySaved = !!props.info?.[targetField];

  const handleCheck = useCallback(async () => {
    if (!isAliasValid) return;

    setIsChecking(true);
    setResult(null);
    setCheckError(null);

    try {
      const response: CheckZelleAliasResponse = await checkZelleAlias({ aliasField: selectedField, applicantUid: props.uid });

      if (response?.error) {
        setCheckError(response.error);
        return;
      }

      if (response?.aliasEnrollmentStatus === "TOKEN_ENROLLED") {
        // Save the actual display value (phone/email) so payment processing can use it
        if (targetField) { 
          props.setInfoKey(targetField, alias, true, false);
          props.setInfoKey(targetField + "_details", JSON.stringify(response), true, false);
        }
      } else {
        if (targetField) {
          props.setInfoKey(targetField, "", false, false);
          props.setInfoKey(targetField + "_details", "", false, false);
        }
      }
      setResult(response);
    } catch (e) {
      if (targetField) { 
        props.setInfoKey(targetField, "", false, false);
        props.setInfoKey(targetField + "_details", "", false, false);
      }
      setCheckError(L.applicant.zelle_intake.unexpected_error);
    } finally {
      setIsChecking(false);
    }
  }, [selectedField, alias, checkZelleAlias, isAliasValid, props, targetField, L]);

  const handleClear = () => {
    setAlias("");
    setSelectedField("");
    setResult(null);
    setCheckError(null);
    if (targetField) {
      props.setInfoKey(targetField, "", false, false);
      props.setInfoKey(targetField + "_details", "", false, false);
    }
    setShowClearConfirm(false);
  };

  const handleRadioChange = (value: string) => {
    // value is the field name; option.label is the actual phone/email to display
    const selectedOption = contactOptions.find((option) => option.value === value);
    setSelectedField(value);
    setAlias(selectedOption?.label ?? "");
    setResult(null);
    setCheckError(null);
    if (targetField && isAlreadySaved) {
      props.setInfoKey(targetField, "", false, false);
    }
  };

  if (isChecking) {
    return <Loading />;
  }

  return (
    <div className="max-w-md">
      <p className="">{L.applicant.zelle_intake.intro_contact_options}</p>

      <div className="flex flex-col gap-1.5 max-w-sm">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex flex-col gap-2">
              {contactOptions.map((option) => (
                <Form.Check
                  key={`${radioGroupName}-${option.value}`}
                  type="radio"
                  name={radioGroupName}
                  value={option.value}
                  checked={selectedField === option.value}
                  onChange={() => handleRadioChange(option.value)}
                  disabled={isAlreadySaved && isEnrolled}
                  label={option.label}
                />
              ))}
            </div>
          </div>
          {isAlreadySaved && isEnrolled && (
            <Button
              variant="secondary"
              onClick={() => setShowClearConfirm(true)}
              className="shrink-0"
              icon={<Icon name="TrashIcon" />}
            />
          )}
        </div>
      </div>

      {!isAlreadySaved && (
        <Button
          onClick={handleCheck}
          isDisabled={!isAliasValid}
          className="mt-3"
        >
          {L.applicant.zelle_intake.check_enrollment_button}
        </Button>
      )}

      <Modal
        open={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        title={L.applicant.zelle_intake.remove_modal_title}
        size="sm"
      >
        <p className="text-sm text-gray-700 mb-6">
          {L.applicant.zelle_intake.remove_modal_body.replace('$alias', alias)}
        </p>
        <div className="flex gap-3 justify-end">
          <Button variant="secondary" onClick={() => setShowClearConfirm(false)}>
            {L.applicant.zelle_intake.remove_modal_cancel}
          </Button>
          <Button variant="danger" onClick={handleClear}>
            {L.applicant.zelle_intake.remove_modal_confirm}
          </Button>
        </div>
      </Modal>

      {result !== null && !checkError && (
        <>
          {isEnrolled && (
            <Alert variant="success" className="mt-4">
              <div className="flex flex-col gap-1">
                <span className="font-semibold">{L.applicant.zelle_intake.enrolled_title}</span>
                {(result.firstName || result.lastName) && (
                  <span>
                    {L.applicant.zelle_intake.name_on_account
                      .replace('$firstName', result.firstName ?? '')
                      .replace('$lastName', result.lastName ?? '')}
                  </span>
                )}
                {result.financialOrganizationName && (
                  <span>
                    {L.applicant.zelle_intake.bank
                      .replace('$financialOrganizationName', result.financialOrganizationName)}
                  </span>
                )}
              </div>
            </Alert>
          )}

          {!isEnrolled && (
            <Alert variant="warning" className="mt-4">
              <div className="flex flex-col gap-1">
                <span className="font-semibold">{L.applicant.zelle_intake.not_enrolled_title}</span>
                <span>{L.applicant.zelle_intake.not_enrolled_contact}</span>
              </div>
            </Alert>
          )}
        </>
      )}

      {checkError && (
        <Alert variant="danger" className="mt-4">
          <div className="flex flex-col gap-1">
            <span className="font-semibold">{L.applicant.zelle_intake.error_title}</span>
            <span>{checkError}</span>
          </div>
        </Alert>
      )}
    </div>
  );
}
